/**
 * GestioneCompetizioniPage — admin crea/lista competizioni per stagione.
 *
 * Crea competizione (Campionato / Champions League / Supercoppa) con:
 * - Squadre: multiselect dalle squadre della lega
 * - Giornate fanta: multiselect dalle giornate della stagione (solo campionato)
 * - Vincenti campionato/CL: dropdown competizioni concluse (per supercoppa)
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  TipoCompetizione,
  useAvanzaFaseCL,
  useCompetizioniStagione,
  useCreaCL,
  useCreaCampionato,
  useCreaSupercoppa,
  useEliminaCompetizione,
  useRinominaCompetizione,
} from "../../hooks/useFase5";
import { useSquadre } from "../../hooks/useFase7";
import { useAuthStore } from "../../lib/auth-store";
import { api } from "../../lib/api";
import { MobileShell } from "../shell/MobileShell";
import { StagioneSelector } from "../common/StagioneSelector";
import { etichetta } from "../common/etichette";
import { tr } from "../../i18n";

interface GiornataLight {
  id: string;
  stagione_id: string;
  numero: number;
  numero_serie_a: number;
  stato: string;
}

type FormTipo = "campionato" | "cl" | "supercoppa";

const TIPO_EMOJI: Record<TipoCompetizione, string> = {
  campionato: "🏆",
  champions_league: "⭐",
  supercoppa: "👑",
};

export function GestioneCompetizioniPage() {
  useAuth(); // session check
  const isAuth = !!useAuthStore((s) => s.accessToken);
  const [stagioneId, setStagioneId] = useState("");
  const [formTipo, setFormTipo] = useState<FormTipo>("campionato");
  const [nome, setNome] = useState("");
  const [squadreSel, setSquadreSel] = useState<Set<string>>(new Set());
  const [giornateSel, setGiornateSel] = useState<Set<string>>(new Set());
  const [vincenteCampId, setVincenteCampId] = useState("");
  const [vincenteClId, setVincenteClId] = useState("");
  const [seed, setSeed] = useState("");
  // Struttura CL (Andrea 2026-09-01)
  const [qualificate, setQualificate] = useState<"4" | "2">("4");
  const [arQuarti, setArQuarti] = useState(true);
  const [arSemi, setArSemi] = useState(true);
  const [arFinale, setArFinale] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Formato CL dal numero di squadre (Andrea 2026-10-01): fino a 7 girone
  // unico, da 8 a 10 due gironi (4+4, 5+4, 5+5). Stesse regole del backend.
  const nSq = squadreSel.size;
  const dimensioniCL: number[] | null =
    nSq >= 4 && nSq <= 10
      ? nSq <= 7
        ? [nSq]
        : [nSq - Math.floor(nSq / 2), Math.floor(nSq / 2)]
      : null;
  const dueGironi = (dimensioniCL?.length ?? 2) === 2;
  const turniGironi = dimensioniCL
    ? Math.max(...dimensioniCL.map((d) => (d % 2 === 0 ? 2 * (d - 1) : 2 * d)))
    : 6;
  // Fasi KO: 2 gironi → quarti (4) o semi (2); girone unico → semi (4) o finale (2)
  const conQuartiCL = dueGironi && qualificate === "4";
  const conSemiCL = dueGironi || qualificate === "4";
  const turniClRichiesti =
    turniGironi +
    (conQuartiCL ? (arQuarti ? 2 : 1) : 0) +
    (conSemiCL ? (arSemi ? 2 : 1) : 0) +
    (arFinale ? 2 : 1);

  const { data: lista } = useCompetizioniStagione(stagioneId || null);
  const { data: squadre } = useSquadre();
  const { data: giornate } = useQuery({
    queryKey: ["stagione-giornate", stagioneId],
    queryFn: async () =>
      (await api.get<GiornataLight[]>(`/admin/stagioni/${stagioneId}/giornate`)).data,
    enabled: isAuth && !!stagioneId,
  });

  const creaCampMut = useCreaCampionato();
  const creaClMut = useCreaCL();
  const avanzaMut = useAvanzaFaseCL();
  const rinominaMut = useRinominaCompetizione();
  const creaSupMut = useCreaSupercoppa();
  const eliminaMut = useEliminaCompetizione();

  // Reset selezioni quando cambia tipo/stagione
  useEffect(() => {
    setSquadreSel(new Set());
    setGiornateSel(new Set());
    setVincenteCampId("");
    setVincenteClId("");
    setSeed("");
    setErrore(null);
    setSuccess(null);
  }, [formTipo, stagioneId]);

  // Competizioni esistenti filtrate per tipo (per dropdown supercoppa)
  const campionatiConclusi =
    lista?.filter((c) => c.tipo === "campionato" && c.stato === "conclusa") ?? [];
  const clConclusi =
    lista?.filter((c) => c.tipo === "champions_league" && c.stato === "conclusa") ?? [];

  function toggleSquadra(id: string) {
    setSquadreSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleGiornata(id: string) {
    setGiornateSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selezionaTutteSquadre() {
    setSquadreSel(new Set(squadre?.map((s) => s.id) ?? []));
  }
  function deselezionaSquadre() {
    setSquadreSel(new Set());
  }
  function selezionaTutteGiornate() {
    setGiornateSel(new Set(giornate?.map((g) => g.id) ?? []));
  }
  function deselezionaGiornate() {
    setGiornateSel(new Set());
  }

  async function submit() {
    if (!stagioneId.trim()) return;
    setErrore(null);
    setSuccess(null);
    try {
      if (formTipo === "campionato") {
        if (squadreSel.size === 0) {
          throw new Error(tr("Seleziona almeno una squadra"));
        }
        if (giornateSel.size === 0) {
          throw new Error(tr("Seleziona almeno una giornata"));
        }
        const r = await creaCampMut.mutateAsync({
          stagioneId,
          payload: {
            squadre_ids: Array.from(squadreSel),
            giornate_fanta_ids: Array.from(giornateSel),
            nome: nome || "Campionato",
          },
        });
        setSuccess(tr("Campionato creato: {{v1}} partite", { v1: r.partite_create }));
      } else if (formTipo === "cl") {
        if (!dimensioniCL) {
          throw new Error(tr("La coppa va da 4 a 10 squadre"));
        }
        if (giornateSel.size !== turniClRichiesti) {
          throw new Error(
            tr("Con questa struttura servono esattamente {{v1}} giornate ", {
              v1: turniClRichiesti,
            }) +
              tr("({{v1}} gironi + fase a eliminazione), selezionate {{v2}}", {
                v1: turniGironi,
                v2: giornateSel.size,
              }),
          );
        }
        // Ordina per numero giornata: le prime 6 vanno ai gironi, le
        // successive a quarti/semi/finale nell'ordine.
        const giornateOrdinate = (giornate ?? [])
          .filter((g) => giornateSel.has(g.id))
          .sort((a, b) => a.numero - b.numero)
          .map((g) => g.id);
        const r = await creaClMut.mutateAsync({
          stagioneId,
          payload: {
            squadre_ids: Array.from(squadreSel),
            nome: nome || "Coppa",
            seed: seed ? Number(seed) : null,
            giornate_fanta_ids: giornateOrdinate,
            qualificate_per_girone: Number(qualificate),
            andata_ritorno_quarti: arQuarti,
            andata_ritorno_semi: arSemi,
            finale_andata_ritorno: arFinale,
          },
        });
        setSuccess(
          tr("CL creata: {{v1}} partite gironi. Le fasi a ", { v1: r.partite_create }) +
            tr('eliminazione si generano col bottone "Avanza fase" quando i ') +
            tr("gironi sono completi."),
        );
      } else {
        if (!vincenteCampId || !vincenteClId) {
          throw new Error(tr("Seleziona il vincente del campionato e quello della CL"));
        }
        await creaSupMut.mutateAsync({
          stagioneId,
          payload: {
            vincente_campionato_id: vincenteCampId,
            vincente_cl_id: vincenteClId,
            nome: tr("Supercoppa"),
          },
        });
        setSuccess(tr("Supercoppa creata (1 partita gara secca)"));
      }
    } catch (e) {
      const err = e as {
        response?: { data?: { detail?: unknown }; status?: number };
        message?: string;
      };
      const raw = err.response?.data?.detail;
      // FastAPI può ritornare detail come stringa (HTTPException) OPPURE
      // come array di {loc, msg, type} (validazione Pydantic 422).
      // Stringifico sempre per evitare di passare un oggetto a setErrore →
      // renderizzare oggetti come children fa crashare React (schermo bianco).
      let msg: string;
      if (typeof raw === "string") {
        msg = raw;
      } else if (Array.isArray(raw)) {
        msg = raw
          .map((d: { loc?: unknown[]; msg?: string }) => {
            const loc = Array.isArray(d.loc) ? d.loc.join(".") : "";
            return tr("{{v1}}: {{v2}}", { v1: loc, v2: d.msg ?? JSON.stringify(d) });
          })
          .join("; ");
      } else if (raw !== undefined && raw !== null) {
        try {
          msg = JSON.stringify(raw);
        } catch {
          msg = String(raw);
        }
      } else {
        msg = err.message ?? tr("errore sconosciuto");
      }
      const status = err.response?.status ? tr(" (HTTP {{v1}})", { v1: err.response.status }) : "";
      setErrore(msg + status);
    }
  }

  const pendingAny = creaCampMut.isPending || creaClMut.isPending || creaSupMut.isPending;

  return (
    <MobileShell logoSuffix="COMPETIZIONI">
      <h2 className="font-display text-3xl text-accent tracking-wide">
        {tr("Gestione competizioni")}
      </h2>

      <div className="card">
        <StagioneSelector value={stagioneId} onChange={setStagioneId} />
      </div>

      {stagioneId && (
        <>
          {/* Lista competizioni esistenti */}
          <div className="card p-0 overflow-hidden">
            <div className="bg-primary-deep/40 px-4 py-2 text-xs font-bold uppercase tracking-wider">
              {tr("Competizioni della stagione")}
            </div>
            {lista && lista.length === 0 && (
              <div className="p-6 text-center text-fg-muted">
                {tr("Nessuna competizione ancora creata.")}
              </div>
            )}
            {lista?.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-3 p-4 border-b border-primary/10 last:border-b-0"
              >
                <span className="text-2xl">{TIPO_EMOJI[c.tipo]}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold">{c.nome}</div>
                  <div className="text-xs text-fg-muted mt-1">
                    {etichetta(c.tipo)} · stato {etichetta(c.stato)}
                    {c.congelata_at && tr(" · ⚙️ congelata")}
                  </div>
                </div>
                <Link to={`/lega?comp=${c.id}`} className="btn-ghost text-sm">
                  {tr("Vedi")}
                </Link>
                {c.tipo !== "supercoppa" && (
                  <button
                    type="button"
                    className="btn-ghost text-sm"
                    disabled={rinominaMut.isPending}
                    title={tr("Cambia il nome della competizione")}
                    onClick={async () => {
                      const nuovo = prompt(tr('Nuovo nome per "{{v1}}":', { v1: c.nome }), c.nome);
                      const pulito = nuovo?.trim();
                      if (!pulito || pulito === c.nome) return;
                      try {
                        await rinominaMut.mutateAsync({
                          competizioneId: c.id,
                          stagioneId,
                          nome: pulito,
                        });
                      } catch (e) {
                        const err = e as { response?: { data?: { detail?: string } } };
                        alert(err.response?.data?.detail ?? tr("Errore nel rinominare"));
                      }
                    }}
                  >
                    ✏️
                  </button>
                )}
                {c.tipo === "champions_league" && (
                  <button
                    type="button"
                    disabled={avanzaMut.isPending}
                    onClick={async () => {
                      try {
                        const r = await avanzaMut.mutateAsync({
                          competizioneId: c.id,
                          stagioneId,
                        });
                        alert(
                          tr('✓ Fase "{{v1}}" creata ({{v2}} partite):\\n\\n', {
                            v1: r.fase_creata,
                            v2: r.partite_create,
                          }) + r.accoppiamenti.join("\n"),
                        );
                      } catch (e) {
                        const err = e as {
                          response?: { data?: { detail?: string } };
                          message?: string;
                        };
                        alert(
                          `Avanzamento non possibile: ${
                            err.response?.data?.detail ?? err.message ?? "errore"
                          }`,
                        );
                      }
                    }}
                    className="btn-ghost text-sm border border-accent/40 text-accent disabled:opacity-40"
                    title={tr(
                      tr(
                        "Crea la fase successiva (quarti/semi/finale) quando la precedente è completa",
                      ),
                    )}
                  >
                    {tr("▶ Avanza fase")}
                  </button>
                )}
                <button
                  type="button"
                  disabled={
                    !!c.congelata_at ||
                    (eliminaMut.isPending && eliminaMut.variables?.competizioneId === c.id)
                  }
                  onClick={() => {
                    if (
                      !confirm(
                        tr(
                          'Eliminare la competizione "{{v1}}"?\\n\\nVerranno cancellate tutte le partite e le formazioni associate.\\nQuesta azione è IRREVERSIBILE.',
                          { v1: c.nome },
                        ),
                      )
                    )
                      return;
                    eliminaMut.mutate(
                      { competizioneId: c.id, stagioneId },
                      {
                        onError: (e) => {
                          const err = e as {
                            response?: { data?: { detail?: string } };
                            message?: string;
                          };
                          alert(
                            `Errore eliminazione: ${
                              err.response?.data?.detail ?? err.message ?? "sconosciuto"
                            }`,
                          );
                        },
                      },
                    );
                  }}
                  className="btn-ghost text-sm border border-danger/40 text-danger hover:bg-danger/10 disabled:opacity-30"
                  title={
                    c.congelata_at
                      ? tr("Competizione congelata: non eliminabile")
                      : tr("Elimina competizione")
                  }
                >
                  🗑
                </button>
              </div>
            ))}
          </div>

          {/* Form creazione */}
          <div className="card space-y-3">
            <h3 className="font-bold text-accent">{tr("Crea nuova competizione")}</h3>

            <div className="flex gap-2 flex-wrap">
              {(["campionato", "cl", "supercoppa"] as FormTipo[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setFormTipo(t)}
                  className={`pill ${
                    formTipo === t ? "bg-accent text-ink" : "bg-primary-deep/40 text-fg-muted"
                  }`}
                >
                  {t === "campionato"
                    ? tr("🏆 Campionato")
                    : t === "cl"
                      ? tr("⭐ Coppa")
                      : tr("👑 Supercoppa")}
                </button>
              ))}
            </div>

            {/* Andrea 2026-10-08: campionato e coppa col nome che vuole la
                lega; la Supercoppa (e la SquadCup) si chiamano cosi' per tutti */}
            {formTipo === "supercoppa" ? (
              <div className="text-xs text-fg-muted">
                Si chiama "Supercoppa" per tutte le leghe.
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder={
                    formTipo === "campionato"
                      ? tr("Nome del campionato (es. Serie A, Campionato FantaSquad)")
                      : tr("Nome della coppa (es. Coppa Grifone, Royal Cup)")
                  }
                  className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <div className="text-[11px] text-fg-muted mt-1">
                  Vuoto = {formTipo === "campionato" ? '"Campionato"' : '"Coppa"'}. Si può cambiare
                  anche dopo, dalla lista qui sopra.
                </div>
              </div>
            )}

            {/* === Selettore squadre (campionato + CL) === */}
            {(formTipo === "campionato" || formTipo === "cl") && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-fg-muted uppercase tracking-wider">
                    Squadre partecipanti ({squadreSel.size}/{squadre?.length ?? 0})
                  </label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={selezionaTutteSquadre}
                      className="text-xs text-accent hover:underline"
                    >
                      {tr("Tutte")}
                    </button>
                    <span className="text-xs text-fg-muted">·</span>
                    <button
                      type="button"
                      onClick={deselezionaSquadre}
                      className="text-xs text-fg-muted hover:text-fg"
                    >
                      {tr("Nessuna")}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {(squadre ?? []).map((s) => (
                    <label
                      key={s.id}
                      className="flex items-center gap-2 px-3 py-2 bg-primary-deep/40 border border-primary-soft rounded text-sm cursor-pointer hover:border-accent/40"
                    >
                      <input
                        type="checkbox"
                        checked={squadreSel.has(s.id)}
                        onChange={() => toggleSquadra(s.id)}
                        className="accent-accent"
                      />
                      <span className="flex-1 truncate font-bold">{s.nome}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* === Struttura CL (Andrea 2026-09-01) === */}
            {formTipo === "cl" && (
              <div className="space-y-2 border border-primary/20 rounded-lg p-3">
                <div className="text-xs text-fg-muted uppercase tracking-wider">
                  {tr("Struttura fase a eliminazione")}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <label className="flex items-center gap-2">
                    <span className="text-xs text-fg-muted">{tr("Passano ai KO:")}</span>
                    <select
                      value={qualificate}
                      onChange={(e) => setQualificate(e.target.value as "4" | "2")}
                      className="px-2 py-1 bg-primary-deep border border-primary-soft rounded text-sm"
                    >
                      {dueGironi ? (
                        <>
                          <option value="4">tutte e 4 → quarti (gironi = seeding)</option>
                          <option value="2">{tr("prime 2 → semifinali")}</option>
                        </>
                      ) : (
                        <>
                          <option value="4">{tr("prime 4 → semifinali")}</option>
                          <option value="2">{tr("prime 2 → finale diretta")}</option>
                        </>
                      )}
                    </select>
                  </label>
                  {conQuartiCL && (
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={arQuarti}
                        onChange={(e) => setArQuarti(e.target.checked)}
                      />
                      {tr("Quarti A/R")}
                    </label>
                  )}
                  {conSemiCL && (
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={arSemi}
                        onChange={(e) => setArSemi(e.target.checked)}
                      />
                      {tr("Semifinali A/R")}
                    </label>
                  )}
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={arFinale}
                      onChange={(e) => setArFinale(e.target.checked)}
                    />
                    {tr("Finale A/R")}
                  </label>
                </div>
                <div className="text-xs text-fg-muted">
                  {dimensioniCL ? (
                    <>
                      {tr("Formato con")} <b className="text-fg">{nSq}</b> squadre:{" "}
                      {dueGironi
                        ? tr("due gironi da {{v1}} e {{v2}}", {
                            v1: dimensioniCL[0],
                            v2: dimensioniCL[1],
                          })
                        : tr("girone unico da {{v1}}", { v1: nSq })}
                      {dimensioniCL.some((d) => d % 2 === 1)
                        ? tr(" (una squadra riposa a turno)")
                        : ""}
                      . Giornate necessarie: <b className="text-accent">{turniClRichiesti}</b> —{" "}
                      {turniGironi} per i gironi +{" "}
                      {conQuartiCL ? tr("{{v1}} quarti + ", { v1: arQuarti ? 2 : 1 }) : ""}
                      {conSemiCL ? tr("{{v1}} semifinali + ", { v1: arSemi ? 2 : 1 }) : ""}
                      {arFinale ? 2 : 1} finale. Selezionale qui sotto: le prime (per numero) vanno
                      ai gironi, le ultime alla fase a eliminazione.
                    </>
                  ) : (
                    <>{tr("Seleziona da 4 a 10 squadre per vedere il formato.")}</>
                  )}
                </div>
              </div>
            )}

            {/* === Selettore giornate (campionato + CL) === */}
            {(formTipo === "campionato" || formTipo === "cl") && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-fg-muted uppercase tracking-wider">
                    Giornate fanta ({giornateSel.size}/{giornate?.length ?? 0})
                    {formTipo === "cl" && (
                      <span
                        className={
                          giornateSel.size === turniClRichiesti ? "text-success" : "text-warning"
                        }
                      >
                        {" "}
                        — ne servono {turniClRichiesti}
                      </span>
                    )}
                  </label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={selezionaTutteGiornate}
                      className="text-xs text-accent hover:underline"
                    >
                      {tr("Tutte")}
                    </button>
                    <span className="text-xs text-fg-muted">·</span>
                    <button
                      type="button"
                      onClick={deselezionaGiornate}
                      className="text-xs text-fg-muted hover:text-fg"
                    >
                      {tr("Nessuna")}
                    </button>
                  </div>
                </div>
                {!giornate?.length ? (
                  <div className="text-xs text-fg-muted italic px-3 py-2 bg-primary-deep/20 rounded">
                    {tr(
                      tr(
                        "Nessuna giornata creata. Le giornate vengono create con la stagione (range da `giornata_serie_a_inizio` a `_fine`). Controlla i settaggi stagione.",
                      ),
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-1 max-h-72 overflow-y-auto p-1">
                    {giornate.map((g) => (
                      <label
                        key={g.id}
                        className={`flex flex-col items-center gap-0.5 px-2 py-1.5 border rounded text-xs cursor-pointer ${
                          giornateSel.has(g.id)
                            ? "bg-accent/20 border-accent text-accent"
                            : "bg-primary-deep/40 border-primary-soft hover:border-accent/40"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={giornateSel.has(g.id)}
                          onChange={() => toggleGiornata(g.id)}
                          className="hidden"
                        />
                        <span className="font-bold font-mono">G{g.numero}</span>
                        <span className="text-[9px] text-fg-muted">SA {g.numero_serie_a}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* === Seed CL === */}
            {formTipo === "cl" && (
              <div>
                <label className="text-xs text-fg-muted uppercase tracking-wider">
                  {tr("Seed sorteggio (opzionale)")}
                </label>
                <input
                  type="number"
                  value={seed}
                  onChange={(e) => setSeed(e.target.value)}
                  placeholder={tr("es. 42")}
                  className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <p className="text-xs text-fg-muted mt-1">
                  Lascia vuoto per random. Stesso seed = stesso sorteggio.
                </p>
              </div>
            )}

            {/* === Supercoppa: dropdown vincenti === */}
            {formTipo === "supercoppa" && (
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-fg-muted uppercase tracking-wider">
                    {tr("Vincente campionato")}
                  </label>
                  <select
                    value={vincenteCampId}
                    onChange={(e) => setVincenteCampId(e.target.value)}
                    className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
                  >
                    <option value="">{tr("— seleziona campionato concluso —")}</option>
                    {campionatiConclusi.map((c) => (
                      <option key={c.id} value={c.id}>
                        🏆 {c.nome}
                      </option>
                    ))}
                  </select>
                  {campionatiConclusi.length === 0 && (
                    <p className="text-xs text-warning mt-1">
                      {tr("⚠ Nessun campionato concluso in questa stagione.")}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-fg-muted uppercase tracking-wider">
                    {tr("Vincente CL")}
                  </label>
                  <select
                    value={vincenteClId}
                    onChange={(e) => setVincenteClId(e.target.value)}
                    className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
                  >
                    <option value="">{tr("— seleziona CL conclusa —")}</option>
                    {clConclusi.map((c) => (
                      <option key={c.id} value={c.id}>
                        ⭐ {c.nome}
                      </option>
                    ))}
                  </select>
                  {clConclusi.length === 0 && (
                    <p className="text-xs text-warning mt-1">
                      {tr("⚠ Nessuna CL conclusa in questa stagione.")}
                    </p>
                  )}
                </div>
              </div>
            )}

            {errore && <div className="text-sm text-danger bg-danger/10 rounded p-2">{errore}</div>}
            {success && (
              <div className="text-sm text-success bg-success/10 rounded p-2">{success}</div>
            )}

            <button
              type="button"
              onClick={submit}
              disabled={pendingAny}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {pendingAny ? tr("Creazione…") : tr("Crea competizione")}
            </button>
          </div>
        </>
      )}
    </MobileShell>
  );
}
